/**
 * Lightweight export helpers — no external dependencies.
 *
 *   downloadCsv()  — emits a UTF-8 CSV with a BOM so Excel auto-detects
 *                    encoding. Saved with a .csv extension; Excel opens
 *                    it natively as a spreadsheet (formulas not stored,
 *                    only the formula text in a separate column).
 *   printPdf()     — opens a print-friendly popup window with the table
 *                    HTML and triggers window.print(). The user picks
 *                    "Save as PDF" in the print dialog.
 *
 * Both take the same shape:
 *   { title, columns: [{ key, label, formula? }], rows: [{ [key]: value, _formula?: { [key]: "string" } }] }
 *
 * If `includeFormula` is true, a trailing "Formula" column is added.
 * `_formula` on a row (or `column.formula`) supplies the expression text;
 * falls back to blank.
 */

const escapeCsv = (v) => {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const buildColumnsWithFormula = (columns, includeFormula) =>
  includeFormula
    ? [...columns, { key: "_formula_col", label: "Formula" }]
    : columns;

const cellForCsv = (row, col) => {
  if (col.key === "_formula_col") {
    return (row._formula && row._formula.__row__) || row._formula_text || "";
  }
  const v = row[col.key];
  return v == null ? "" : v;
};

export function downloadCsv({ title, columns, rows, includeFormula = false }) {
  const cols = buildColumnsWithFormula(columns, includeFormula);
  const head = cols.map((c) => escapeCsv(c.label)).join(",");
  const body = rows.map((r) => cols.map((c) => escapeCsv(cellForCsv(r, c))).join(",")).join("\n");
  // UTF-8 BOM so Excel detects the encoding correctly.
  const csv = "﻿" + head + "\n" + body + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeName(title || "export")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function printPdf({ title, columns, rows, includeFormula = false }) {
  const cols = buildColumnsWithFormula(columns, includeFormula);
  const safeTitle = (title || "Report").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const headRow = cols.map((c) =>
    `<th>${(c.label || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</th>`).join("");
  const bodyRows = rows.map((r) => {
    const tds = cols.map((c) => {
      const v = cellForCsv(r, c);
      const text = v == null ? "" : String(v);
      const aligned = typeof v === "number" ? ` style="text-align:right"` : "";
      return `<td${aligned}>${text.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</td>`;
    }).join("");
    return `<tr>${tds}</tr>`;
  }).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 24px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 16px; }
  .meta { color: #555; font-size: 11px; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #d0d4dc; padding: 6px 8px; text-align: left; vertical-align: top; }
  thead { background: #0b3a64; color: #fff; }
  tbody tr:nth-child(even) { background: #f6f8fc; }
  @media print { body { padding: 0; } }
</style></head><body>
  <h1>${safeTitle}</h1>
  <div class="meta">Generated ${new Date().toLocaleString()}</div>
  <table><thead><tr>${headRow}</tr></thead><tbody>${bodyRows}</tbody></table>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 100); };</script>
</body></html>`;

  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) {
    // eslint-disable-next-line no-alert
    alert("Popup blocked — allow popups for this site to download as PDF.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function safeName(s) {
  return String(s).replace(/[^a-z0-9\-_\.]+/gi, "_").replace(/^_+|_+$/g, "");
}
